import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBookmarks, Bookmark } from '../hooks/useBookmarks';
import { useCollections, Collection } from '../hooks/useCollections';
import InlineNoteEditor from '../components/InlineNoteEditor';
import CollectionSidebar from '../components/CollectionSidebar';
import CollectionModal from '../components/CollectionModal';
import MoveToCollectionMenu from '../components/MoveToCollectionMenu';
import JudgmentModal from '../components/JudgmentModal';
import { useTranslation } from 'react-i18next';

export default function BookmarksPage() {
  const { getBookmarks, removeBookmark, updateBookmarkNotes, isLoading: bookmarksLoading } = useBookmarks();
  const {
    collections,
    isLoading: collectionsLoading,
    createCollection,
    updateCollection,
    deleteCollection,
    moveBookmarkToCollection,
    fetchCollections,
  } = useCollections();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [total, setTotal] = useState(0);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);

  // Collection modal state
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);

  // Mobile sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Judgment modal state
  const [selectedBookmark, setSelectedBookmark] = useState<Bookmark | null>(null);

  useEffect(() => {
    loadBookmarks();
  }, [page, selectedCollectionId]);

  // Load uncategorized count separately
  useEffect(() => {
    loadUncategorizedCount();
  }, []);

  const loadBookmarks = async () => {
    const collectionIdParam = selectedCollectionId === 'uncategorized'
      ? 'uncategorized'
      : selectedCollectionId || undefined;

    const result = await getBookmarks(page, 20, collectionIdParam);
    if (result) {
      setBookmarks(result.bookmarks);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    }
  };

  const loadUncategorizedCount = async () => {
    const result = await getBookmarks(1, 1, 'uncategorized');
    if (result) {
      setUncategorizedCount(result.total);
    }
  };

  const handleRemove = async (judgmentId: number) => {
    const success = await removeBookmark(judgmentId);
    if (success) {
      setBookmarks((prev) => prev.filter((b) => b.judgmentId !== judgmentId));
      setTotal((prev) => prev - 1);
      // Refresh collections to update counts
      fetchCollections();
      loadUncategorizedCount();
    }
  };

  const handleSelectCollection = (id: string | null) => {
    setSelectedCollectionId(id);
    setPage(1);
    setIsSidebarOpen(false);
  };

  const handleCreateCollection = async (name: string, description?: string) => {
    setIsCreatingCollection(true);
    try {
      const result = await createCollection(name, description);
      if (!result) {
        throw new Error(t('bookmarks.createCollectionError'));
      }
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleUpdateCollection = async (name: string, description?: string) => {
    if (!editingCollection) return;

    setIsCreatingCollection(true);
    try {
      const result = await updateCollection(editingCollection.id, { name, description });
      if (!result) {
        throw new Error(t('bookmarks.updateCollectionError'));
      }
    } finally {
      setIsCreatingCollection(false);
    }
  };

  const handleDeleteCollection = async (id: string) => {
    const success = await deleteCollection(id);
    if (success && selectedCollectionId === id) {
      setSelectedCollectionId(null);
    }
    // Refresh bookmarks as they might have moved to uncategorized
    loadBookmarks();
    loadUncategorizedCount();
  };

  const handleMoveBookmark = async (bookmarkId: string, collectionId: string | null) => {
    const success = await moveBookmarkToCollection(collectionId, bookmarkId);
    if (success) {
      // Update bookmark in local state
      setBookmarks((prev) =>
        prev.map((b) =>
          b.id === bookmarkId ? { ...b, collectionId } : b
        )
      );
      // Refresh to get updated list if we're filtering by collection
      if (selectedCollectionId) {
        loadBookmarks();
      }
      loadUncategorizedCount();
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('bookmarks.noDate');
    return new Date(dateString).toLocaleDateString(locale);
  };

  const courtTypeLabels: Record<string, string> = {
    COMMON: t('courtType.common'),
    SUPREME: t('courtType.supreme'),
    ADMINISTRATIVE: t('courtType.administrative'),
    CONSTITUTIONAL_TRIBUNAL: t('courtType.constitutionalTribunal'),
    NATIONAL_APPEAL_CHAMBER: t('courtType.nationalAppealChamber'),
  };

  const getCollectionName = () => {
    if (selectedCollectionId === null) return t('bookmarks.allBookmarks');
    if (selectedCollectionId === 'uncategorized') return t('bookmarks.uncategorized');
    const collection = collections.find((c) => c.id === selectedCollectionId);
    return collection?.name || t('bookmarks.bookmarks');
  };

  const isLoading = bookmarksLoading || collectionsLoading;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex gap-6">
        {/* Sidebar - hidden on mobile, shown on desktop */}
        <div className="hidden md:block">
          <CollectionSidebar
            collections={collections}
            selectedCollectionId={selectedCollectionId}
            uncategorizedCount={uncategorizedCount}
            totalCount={total}
            onSelectCollection={handleSelectCollection}
            onCreateCollection={() => {
              setEditingCollection(null);
              setIsCollectionModalOpen(true);
            }}
            onEditCollection={(collection) => {
              setEditingCollection(collection);
              setIsCollectionModalOpen(true);
            }}
            onDeleteCollection={handleDeleteCollection}
            isLoading={collectionsLoading}
          />
        </div>

        {/* Main content */}
        <div className="flex-1">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  {/* Mobile menu button */}
                  <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="md:hidden p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {getCollectionName()}
                  </h1>
                </div>
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  {total} {total === 1 ? t('bookmarks.savedJudgmentSingular') : t('bookmarks.savedJudgmentsPlural')}
                </p>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : bookmarks.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                {selectedCollectionId ? t('bookmarks.noBookmarksInCollection') : t('bookmarks.noBookmarks')}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {selectedCollectionId
                  ? t('bookmarks.noBookmarksInCollectionDesc')
                  : t('bookmarks.noBookmarksDesc')}
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {t('bookmarks.searchJudgments')}
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {bookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {courtTypeLabels[bookmark.judgment.courtType] || bookmark.judgment.courtType}
                          </span>
                          <span className="text-sm text-slate-500 dark:text-slate-400">
                            {formatDate(bookmark.judgment.judgmentDate)}
                          </span>
                        </div>
                        <button
                          onClick={() => setSelectedBookmark(bookmark)}
                          className="font-medium text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left"
                        >
                          {bookmark.judgment.caseNumbers.join(', ') || `${t('bookmarks.judgment')} #${bookmark.judgment.id}`}
                        </button>
                        {bookmark.judgment.summary && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">
                            {bookmark.judgment.summary}
                          </p>
                        )}
                        <InlineNoteEditor
                          value={bookmark.notes}
                          onSave={async (notes) => {
                            const success = await updateBookmarkNotes(bookmark.id, notes);
                            if (success) {
                              setBookmarks((prev) =>
                                prev.map((b) =>
                                  b.id === bookmark.id ? { ...b, notes: notes || null } : b
                                )
                              );
                            }
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <MoveToCollectionMenu
                          collections={collections}
                          currentCollectionId={bookmark.collectionId}
                          onMove={(collectionId) => handleMoveBookmark(bookmark.id, collectionId)}
                          onCreateNew={() => {
                            setEditingCollection(null);
                            setIsCollectionModalOpen(true);
                          }}
                        />
                        <button
                          onClick={() => handleRemove(bookmark.judgmentId)}
                          className="p-2 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          title={t('bookmarks.removeBookmark')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {t('bookmarks.previous')}
                  </button>
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {t('bookmarks.pageOf', { page, totalPages })}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    {t('bookmarks.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        >
          <div
            className="absolute left-0 top-0 bottom-0 w-72 bg-slate-50 dark:bg-slate-900 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('bookmarks.collections')}</h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <CollectionSidebar
              collections={collections}
              selectedCollectionId={selectedCollectionId}
              uncategorizedCount={uncategorizedCount}
              totalCount={total}
              onSelectCollection={handleSelectCollection}
              onCreateCollection={() => {
                setEditingCollection(null);
                setIsCollectionModalOpen(true);
                setIsSidebarOpen(false);
              }}
              onEditCollection={(collection) => {
                setEditingCollection(collection);
                setIsCollectionModalOpen(true);
                setIsSidebarOpen(false);
              }}
              onDeleteCollection={handleDeleteCollection}
              isLoading={collectionsLoading}
            />
          </div>
        </div>
      )}

      {/* Collection modal */}
      <CollectionModal
        isOpen={isCollectionModalOpen}
        onClose={() => {
          setIsCollectionModalOpen(false);
          setEditingCollection(null);
        }}
        onSave={editingCollection ? handleUpdateCollection : handleCreateCollection}
        collection={editingCollection}
        isLoading={isCreatingCollection}
      />

      {/* Judgment modal */}
      {selectedBookmark && (
        <JudgmentModal
          judgmentId={selectedBookmark.judgmentId}
          onClose={() => setSelectedBookmark(null)}
          bookmarkNotes={selectedBookmark.notes}
          onNotesUpdate={async (notes) => {
            const success = await updateBookmarkNotes(selectedBookmark.id, notes);
            if (success) {
              setBookmarks((prev) =>
                prev.map((b) =>
                  b.id === selectedBookmark.id ? { ...b, notes: notes || null } : b
                )
              );
              setSelectedBookmark((prev) => prev ? { ...prev, notes: notes || null } : null);
            }
          }}
        />
      )}
    </div>
  );
}
